import { format } from "date-fns";
import React from "react";

import Typography from "../Text";

const dateSizeOptions = ["small", "medium", "large"] as const;
export const validDelimiters = ["•"] as const;

type DateSizeOption = (typeof dateSizeOptions)[number];
type ValidDelimiter = (typeof validDelimiters)[number];

type BaseFormattedDateTimeProps = Partial<React.ComponentProps<typeof Typography>> & {
	/**
	 * The date object to format
	 */
	date: Date;
	/**
	 * The length of the resulting formatted date/datetime string
	 * @default "small"
	 */
	dateSize?: DateSizeOption;
	/**
	 * A custom date-fns format string to override default options if needed
	 * @default undefined
	 */
	formatString?: string;
};

type FormattedDateTimePropsWithTime = BaseFormattedDateTimeProps & {
	/**
	 * Whether the resulting formatted date/datetime string has a time component
	 * @default true
	 */
	hasTime: true;
	/**
	 * If provided and hasTime is true, a delimiter to separate the date and time components
	 * @default null
	 */
	delimiter?: ValidDelimiter;
};

type FormattedDateTimePropsWithoutTime = BaseFormattedDateTimeProps & {
	/**
	 * Whether the resulting formatted date/datetime string has a time component
	 * @default true
	 */
	hasTime?: false;
};

export type FormattedDateTimeProps = FormattedDateTimePropsWithTime | FormattedDateTimePropsWithoutTime;

type FormatDateTimeOptions =
	| Pick<FormattedDateTimePropsWithTime, "hasTime" | "delimiter" | "dateSize" | "formatString">
	| Pick<FormattedDateTimePropsWithoutTime, "hasTime" | "dateSize" | "formatString">;

// Helper function to check if a delimiter is valid
function isValidDelimiter(delimiter: unknown) {
	return validDelimiters.includes(delimiter as any);
}

/**
 * Helper function to return a date-fns format string using provided parameters
 *
 * @param hasTime A boolean value representing whether the output format has a time component
 * @param delimiter A string value to separate the date and time in formatted string if hasTime is true
 * @param dateSize A string value for the resulting format string's length
 * @returns a date-fns date formatting string
 */
const getDateFormatString = ({
	hasTime,
	delimiter,
	dateSize,
}: {
	hasTime: boolean;
	delimiter?: ValidDelimiter;
	dateSize: DateSizeOption;
}) => {
	let dateFormat: string;
	switch (dateSize) {
		case "small":
			dateFormat = "M/d/yy";
			break;
		case "medium":
			dateFormat = "MMM d, yyyy";
			break;
		case "large":
			dateFormat = "MMMM d, yyyy";
			break;
		default:
			dateFormat = "M/d/yy";
	}

	if (!hasTime) {
		return dateFormat;
	}

	const delimiterStr = delimiter && delimiter.trim() ? ` ${delimiter.trim()} ` : " ";
	let timeFormat: string;

	if (dateSize === "large") {
		timeFormat = "h:mm:ss aaa";
	} else {
		timeFormat = "h:mm aaa";
	}

	return `${dateFormat}${delimiterStr}${timeFormat}`;
};

/**
 * Utility function to format a date object into a standardized date or datetime string.
 * Default formatting is a datetime string of small size.
 *
 * @param date A date object to format
 * @param options An optional object containing formatting options hasTime, dateSize, and formatString
 * @returns a formatted date/datetime string or null if an invalid date object was provided
 */
export const formatDateTime = (date: Date, options?: FormatDateTimeOptions) => {
	const { hasTime = true, dateSize = "small", formatString } = options || {};
	let delimiter: ValidDelimiter | undefined;

	// Check for valid delimiter provided and default to no delimiter if invalid
	if (hasTime && (options as FormattedDateTimePropsWithTime)?.delimiter) {
		const providedDelimiter = (options as FormattedDateTimePropsWithTime).delimiter;
		if (isValidDelimiter(providedDelimiter)) {
			delimiter = providedDelimiter;
		} else {
			console.warn(`Invalid delimiter provided: ${providedDelimiter}. Using default no delimiter.`);
		}
	}

	// Handle invalid date objects passed as a parameter
	if (isNaN(date.getTime())) {
		return null;
	}

	// If formatString is provided, use that to format date object
	let formattedDateString = "";
	if (formatString) {
		formattedDateString = format(date, formatString);
	}
	// Otherwise, get correct format string using hasTime and dateSize params and format date object
	else {
		const dateFormat = getDateFormatString({ hasTime, delimiter, dateSize });
		formattedDateString = format(date, dateFormat);
	}

	// Return formatted date string using date-fns format function
	return formattedDateString;
};

/**
 * React component to format and return a date object
 * @returns a Typography component containing the formatted date/datetime string
 * @deprecated Use `TextDate` from `@performyard/py-components` instead.
 */
const FormattedDateTime = (props: FormattedDateTimeProps) => {
	const { date, hasTime = true, dateSize = "small", formatString } = props;
	let delimiter: ValidDelimiter | undefined;

	// Check for valid delimiter provided and default to no delimiter if invalid
	if (hasTime && (props as FormattedDateTimePropsWithTime).delimiter) {
		const providedDelimiter = (props as FormattedDateTimePropsWithTime).delimiter;
		if (isValidDelimiter(providedDelimiter)) {
			delimiter = providedDelimiter;
		} else {
			console.warn(`Invalid delimiter provided: ${providedDelimiter}. Using default no delimiter.`);
		}
	}

	// Use utility function to obtain formatted date string using props
	const formattedDateString = formatDateTime(date, { hasTime, delimiter, dateSize, formatString });

	// If returned value is null, return null from component
	if (!formattedDateString) {
		return null;
	}

	return (
		<Typography size={props.size ?? "caption"} weight={props.weight ?? "regular"} {...props}>
			{formattedDateString}
		</Typography>
	);
};

/** @deprecated Use `TextDate` from `@performyard/py-components` instead. */
export default FormattedDateTime;
